/*
  **Die Pille bei Mehrfachauswahl** (#291, CONTEXT.md, Listen-Werkzeug-Pille): Welche Befehle für
  die markierten Aufgaben gehen, welche davon in der Pille stehen und welche ins ⋯ rücken — reine
  Rechnung, ohne React. Entschieden im Prototyp `tools/list-toolbar-bulk-prototype` (08.10.2026).

  - Was für KEINE der markierten Aufgaben geht, fehlt — nicht grau.
  - Was für EINIGE geht, steht da und wirkt nur auf die (`fits`).
  - Gemischt gewinnt, was noch etwas zu tun hat: Erledigen, Ruhen lassen, Zu Favoriten.
  - Die Pille hat höchstens drei Plätze, besetzt nach `SELECTION_RANK`; der Rest steht im ⋯. Ein ⋯
    mit einem einzigen Eintrag gibt es nicht — der nimmt dessen Platz.

  Fokus und Erinnerung fehlen hier ganz: Sie gelten einer Aufgabe. „Im Projekt zeigen“ auch — die
  Pille steht nur im Projekt (dort steht man schon) und in Kurz notiert (kein Projekt).
*/

export type SelectionCommandId =
  | 'complete'
  | 'start' | 'deadline' | 'duration' | 'recurrence'
  | 'attention' | 'tags' | 'reward'
  | 'rest' | 'favorite' | 'duplicate' | 'move' | 'nest'
  | 'delete'

/** Die Picker — sie öffnen ihren Kasten (`TaskMenuTargetContent`). */
export type SelectionPickerId = Extract<SelectionCommandId, 'start' | 'deadline' | 'duration' | 'recurrence' | 'attention' | 'tags' | 'reward'>

/** Die Gruppen des Aufgaben-Menüs, im ⋯ durch Striche getrennt; Erledigen steht davor. */
export type SelectionCommandGroup = 'state' | 'time' | 'kind' | 'task' | 'delete'

/**
 * Welche Stellung ein Befehl mit zwei Stellungen gerade hat — sie bestimmt Text, Zeichen und was
 * geschrieben wird.
 */
export type SelectionCommandMode = 'complete' | 'reopen' | 'rest' | 'wake' | 'add' | 'remove'

/** Was die Rechnung über eine markierte Aufgabe wissen muss. */
export interface SelectionTask {
  id: string
  completed: boolean
  /** Ruht (`pausedAt`). */
  dormant: boolean
  favorite: boolean
}

export interface SelectionCommand {
  id: SelectionCommandId
  group: SelectionCommandGroup
  mode?: SelectionCommandMode
  /** Für welche der markierten Aufgaben der Befehl gilt — in ihrer Reihenfolge. Leer: er fehlt. */
  fits: string[]
}

export interface SelectionLayout {
  /** In der Pille, in Menü-Reihenfolge. */
  pill: SelectionCommand[]
  /** Im ⋯, in Menü-Reihenfolge. Leer: kein ⋯. */
  overflow: SelectionCommand[]
}

/** Die Befehle in der Reihenfolge des Aufgaben-Menüs (`TaskMenu`), Erledigen davor (Menü „Aufgaben“). */
export const SELECTION_COMMANDS: readonly { id: SelectionCommandId; group: SelectionCommandGroup }[] = [
  { id: 'complete', group: 'state' },
  { id: 'start', group: 'time' },
  { id: 'deadline', group: 'time' },
  { id: 'duration', group: 'time' },
  { id: 'recurrence', group: 'time' },
  { id: 'attention', group: 'kind' },
  { id: 'tags', group: 'kind' },
  { id: 'reward', group: 'kind' },
  { id: 'rest', group: 'task' },
  { id: 'favorite', group: 'task' },
  { id: 'duplicate', group: 'task' },
  { id: 'move', group: 'task' },
  { id: 'nest', group: 'task' },
  { id: 'delete', group: 'delete' },
]

/** Wer zuerst in die Pille darf: die drei Stammplätze aus #291, dann in Menü-Reihenfolge. */
export const SELECTION_RANK: readonly SelectionCommandId[] = [
  'start', 'move', 'delete',
  'complete', 'deadline', 'duration', 'recurrence', 'attention', 'tags', 'reward',
  'rest', 'favorite', 'duplicate', 'nest',
]

/** Plätze für Befehle in der Pille, wenn die Spalte es hergibt. */
export const SELECTION_MAX_SLOTS = 3

/** Ab so vielen markierten Aufgaben bekommt die Pille die Befehle — eine einzelne ist nur der Cursor. */
export const SELECTION_MIN_TASKS = 2

const PICKERS: ReadonlySet<SelectionCommandId> = new Set(['start', 'deadline', 'duration', 'recurrence', 'attention', 'tags', 'reward'])

export const isSelectionPicker = (id: SelectionCommandId): id is SelectionPickerId => PICKERS.has(id)

const idsOf = (tasks: readonly SelectionTask[]) => tasks.map(task => task.id)

/** Was ein Befehl für DIESE Auswahl ist. */
export function resolveSelectionCommand(id: SelectionCommandId, tasks: readonly SelectionTask[]): SelectionCommand {
  const group = SELECTION_COMMANDS.find(command => command.id === id)!.group
  const open = tasks.filter(task => !task.completed)
  switch (id) {
    case 'complete':
      return open.length
        ? { id, group, mode: 'complete', fits: idsOf(open) }
        : { id, group, mode: 'reopen', fits: idsOf(tasks) }
    case 'rest': {
      const awake = open.filter(task => !task.dormant)
      if (awake.length) return { id, group, mode: 'rest', fits: idsOf(awake) }
      return { id, group, mode: 'wake', fits: idsOf(open) }
    }
    case 'favorite': {
      const notYet = tasks.filter(task => !task.favorite)
      return notYet.length
        ? { id, group, mode: 'add', fits: idsOf(notYet) }
        : { id, group, mode: 'remove', fits: idsOf(tasks) }
    }
    case 'delete':
      return { id, group, fits: idsOf(tasks) }
    // Picker, Duplizieren, Verschieben, Einhängen: nur für offene (wie `appTaskMenuState`).
    default:
      return { id, group, fits: idsOf(open) }
  }
}

/**
 * Pille und ⋯ für eine Auswahl. `unavailable` nimmt heraus, was die Seite nicht anbietet (etwa
 * Verschieben ohne `MoveMenuContext`).
 */
export function layoutSelectionCommands(
  tasks: readonly SelectionTask[],
  { slots, unavailable = [] }: { slots: number; unavailable?: readonly SelectionCommandId[] },
): SelectionLayout {
  const shown = SELECTION_COMMANDS
    .filter(command => !unavailable.includes(command.id))
    .map(command => resolveSelectionCommand(command.id, tasks))
    .filter(command => command.fits.length > 0)
  const byRank = [...shown].sort((a, b) => SELECTION_RANK.indexOf(a.id) - SELECTION_RANK.indexOf(b.id))
  let pill = byRank.slice(0, Math.max(0, slots))
  let overflow = byRank.slice(pill.length)
  if (overflow.length === 1) {
    pill = byRank
    overflow = []
  }
  const menuOrder = (command: SelectionCommand) => SELECTION_COMMANDS.findIndex(entry => entry.id === command.id)
  return {
    pill: pill.sort((a, b) => menuOrder(a) - menuOrder(b)),
    overflow: overflow.sort((a, b) => menuOrder(a) - menuOrder(b)),
  }
}

/** Abstand der Pille zur Spaltenkante (`LIST_TOOLBAR_GUTTER`). */
const GUTTER = 24
/**
 * Breiten der Teile in px (ListToolbar.scss): Griff und Plus mit Polster, der Text „Neue Aufgabe“
 * (Abstand, Wort, das breitere Polster des beschrifteten Knopfs — deutsch, die längere Fassung),
 * Abschnitt, Trenner, der feste Teil der Auswahl (Polster, Anzahl, ⋯) und ein Knopf mit seinem
 * Abstand. Fest statt gemessen wie `LIST_TOOLBAR_SHORT_WIDTH`: Gemessen werden könnte nur, was steht.
 */
const WIDTHS = { add: 56, taskLabel: 104, section: 44, divider: 3, selectionBase: 78, slot: 40 } as const

/** Platz für Knöpfe: die Spalte ohne Abstände, Plus, Abschnitt, Trenner und den festen Teil der Auswahl. */
const roomForSlots = (columnWidth: number, hasSection: boolean) =>
  columnWidth - 2 * GUTTER - WIDTHS.add - (hasSection ? WIDTHS.section : 0) - WIDTHS.divider - WIDTHS.selectionBase

const slotsIn = (room: number, maxSlots: number) => Math.max(1, Math.min(maxSlots, Math.floor(room / WIDTHS.slot)))

/** Wie viele Befehle neben Anzahl und ⋯ in die Spalte passen — höchstens `maxSlots`, mindestens einer. */
export function selectionSlots({ columnWidth, hasSection, maxSlots = SELECTION_MAX_SLOTS }: {
  columnWidth: number
  hasSection: boolean
  maxSlots?: number
}): number {
  return slotsIn(roomForSlots(columnWidth, hasSection), maxSlots)
}

/**
 * Was bei Auswahl in die Spalte passt (Sven, 08.10.2026): **„Neue Aufgabe“ behält seinen Text**, solange
 * daneben alle Plätze passen. Wird es eng, geht zuerst der Text, danach rücken Knöpfe ins ⋯ — Befehle
 * sind wichtiger als ein Wort am Plus. `taskLabel` sagt, ob die Gestalt den Text überhaupt trägt.
 */
export function selectionFit({ columnWidth, hasSection, taskLabel, maxSlots = SELECTION_MAX_SLOTS }: {
  columnWidth: number
  hasSection: boolean
  taskLabel: boolean
  maxSlots?: number
}): { slots: number; taskLabel: boolean } {
  const room = roomForSlots(columnWidth, hasSection)
  if (taskLabel && room - WIDTHS.taskLabel >= maxSlots * WIDTHS.slot) return { slots: maxSlots, taskLabel: true }
  return { slots: slotsIn(room, maxSlots), taskLabel: false }
}
